import { ComponentType } from 'react';
import styled from 'styled-components';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import Tooltip from 'components/shared/Tooltip';
import { simpleDate } from 'lib/date';
import { HighCostMedicationList, HighCostMedicationTier } from 'models/HighCostMedicationList';
import { HighCostMedicationListItem } from 'models/HighCostMedicationListItem';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import InfoIconOutline from 'svg/InfoIconOutline';

import { MedicationCell, RxnormRename } from './rxnormStatus';

const columnHelper = createColumnHelper<HighCostMedicationListItem>();

// Owned here rather than by either row menu: the admin slideout's menu and the customer page's both
// implement it, and typing the injectable RowMenu off the admin component would leave the customer
// column set depending on the admin tree.
export type HighCostMedRowMenuProps = {
  highCostMedicationListItem: HighCostMedicationListItem;
  onEdit: (item: HighCostMedicationListItem) => void;
  onRemove: (item: HighCostMedicationListItem) => void;
};

const EmptyValue = () => <>&mdash;</>;

// The cost tier key, rendered verbatim from the list's unarchived tiers. Tier vocabularies vary per
// pharmacy contract ($–$$$$, "Tier 1/2/3", "High/Medium/Low"), so these are data, not an enum.
function CostTierHeader({ tiers }: { tiers: HighCostMedicationTier[] }) {
  if (!tiers.length) return <>Cost Tier</>;

  return (
    <HeaderWithKey>
      Cost Tier
      <Tooltip
        position='top'
        text={
          <TierKey data-testid='cost-tier-key'>
            {tiers.map((tier) => (
              <TierRow key={tier.id}>
                <TierLabel>{tier.label}</TierLabel>
                <span>{tier.description}</span>
              </TierRow>
            ))}
          </TierKey>
        }>
        {/* The header cell toggles sorting, so the key trigger must not bubble into it. */}
        <KeyTrigger type='button' aria-label='Cost tier key' onClick={(e) => e.stopPropagation()}>
          <InfoIconOutline width={14} height={14} />
        </KeyTrigger>
      </Tooltip>
    </HeaderWithKey>
  );
}

type HighCostMedListColumnsOptions = {
  tiers?: HighCostMedicationList['tiers'];
  onEdit?: (item: HighCostMedicationListItem) => void;
  onRemove?: (item: HighCostMedicationListItem) => void;
  // Injectable so each surface gates its own kebab: the admin slideout passes HighCostMedRowMenu,
  // which checks the admin list-edit permission. Omitted entirely on the read-only customer page.
  RowMenu?: ComponentType<HighCostMedRowMenuProps>;
  // Omitted for a user who cannot edit, whose suggestions then show as plain text.
  onRename?: RxnormRename;
};

export default function highCostMedListColumns({
  tiers = [],
  onEdit,
  onRemove,
  RowMenu,
  onRename,
}: HighCostMedListColumnsOptions = {}) {
  const columns: ColumnDef<HighCostMedicationListItem, any>[] = [
    columnHelper.accessor('genericName', {
      id: 'genericName',
      header: 'Medication',
      cell: (info) => <MedicationCell item={info.row.original} onRename={onRename} />,
      size: 420,
    }),
    columnHelper.accessor('tier', {
      id: 'tier',
      header: () => <CostTierHeader tiers={tiers} />,
      cell: (info) => <BodySmall>{info.getValue()?.label || <EmptyValue />}</BodySmall>,
      size: 120,
    }),
    columnHelper.accessor('dosage', {
      id: 'dosage',
      header: 'Dosage',
      cell: (info) => <BodySmall>{info.getValue() || <EmptyValue />}</BodySmall>,
      size: 120,
    }),
    columnHelper.accessor('route', {
      id: 'route',
      header: 'Route',
      cell: (info) => <BodySmall>{info.getValue() || <EmptyValue />}</BodySmall>,
      size: 120,
    }),
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: 'Date Added',
      cell: (info) => <BodySmall>{simpleDate(info.getValue()) ?? <EmptyValue />}</BodySmall>,
      size: 120,
      // Default-desc column: newest first, matching the API default, so the first header click
      // flips to ascending rather than re-sorting into the order already on screen.
      sortDescFirst: true,
    }),
  ];

  // Only produced when a caller wires up the menu. Pushing it unconditionally leaves a dead 40px
  // column on read-only surfaces. Mirrors denialListColumns.
  if (RowMenu && onEdit && onRemove) {
    const handleEdit = onEdit;
    const handleRemove = onRemove;
    columns.push(
      columnHelper.display({
        id: 'menu',
        cell: (info) => (
          <RowMenu highCostMedicationListItem={info.row.original} onEdit={handleEdit} onRemove={handleRemove} />
        ),
        size: 40,
        meta: { style: { justifyContent: 'flex-end' } },
      })
    );
  }

  return columns;
}

const HeaderWithKey = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const KeyTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: transparent;
  color: ${colors.black50};
  cursor: pointer;
`;

// Two columns shared across every row, so labels and descriptions each line up on their own left
// edge. Tier descriptions are free text, so the second column wraps inside the tooltip's fixed
// width rather than spilling past its edge.
const TierKey = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 12px;
  row-gap: 6px;
  align-items: baseline;
  overflow-wrap: break-word;
`;

const TierRow = styled.div`
  display: contents;
`;

const TierLabel = styled.span`
  font-weight: 500;
  white-space: nowrap;
`;
